/**
 * Headless Quiz Mode session logic.
 *
 * Unlike practice, a quiz gives no hints, no steps, and no correctness feedback
 * during the run. All questions are selected up front, answers are recorded
 * silently, and scoring happens only at completion - producing an overall
 * score plus per-topic and per-subskill breakdowns and a missed-question
 * review with worked solutions.
 */

import type { Difficulty, Question } from "../topics/types";
import type { TopicId } from "../curriculum/curriculum";
import type { SubskillId } from "../curriculum/subskills";
import { gradeSubmission } from "../engine/answer";
import { TaughtSet } from "../curriculum/curriculum";
import {
  selectQuizQuestions,
  scopeLabel,
  type QuizScope,
} from "./question-select";

export interface QuizConfig {
  readonly scope: QuizScope;
  readonly difficulty: Difficulty;
  readonly count: number;
}

interface Recorded {
  readonly question: Question;
  /** Raw student input; empty string if skipped. */
  raw: string;
  correct: boolean;
}

export interface Tally {
  correct: number;
  total: number;
}

export interface QuizResults {
  readonly label: string;
  readonly total: number;
  readonly correct: number;
  readonly percent: number;
  readonly byTopic: ReadonlyMap<TopicId, Tally>;
  readonly bySubskill: ReadonlyMap<SubskillId, Tally>;
  readonly missed: ReadonlyArray<Recorded>;
}

export class QuizSession {
  readonly config: QuizConfig;
  private readonly items: Recorded[];
  private index = 0;
  private finished = false;

  constructor(config: QuizConfig, taught: TaughtSet) {
    this.config = config;
    const questions = selectQuizQuestions(
      config.scope,
      config.difficulty,
      config.count,
      taught,
    );
    this.items = questions.map((q) => ({ question: q, raw: "", correct: false }));
  }

  get position(): number {
    return this.index + 1;
  }
  get total(): number {
    return this.items.length;
  }
  get current(): Question {
    return (this.items[this.index] as Recorded).question;
  }
  get isLast(): boolean {
    return this.index >= this.items.length - 1;
  }
  get isFinished(): boolean {
    return this.finished;
  }

  /** Record the answer for the current question. No grading is shown yet. */
  record(raw: string): void {
    const item = this.items[this.index];
    if (item) item.raw = raw;
  }

  /** Move to the next question; returns false if already on the last. */
  next(): boolean {
    if (this.index >= this.items.length - 1) return false;
    this.index++;
    return true;
  }

  /** Grade everything and lock the quiz. Idempotent. */
  finish(): QuizResults {
    if (!this.finished) {
      for (const item of this.items) {
        item.correct =
          item.raw.trim() !== "" &&
          gradeSubmission(item.raw, item.question.answer);
      }
      this.finished = true;
    }
    return this.results();
  }

  private results(): QuizResults {
    const total = this.items.length;
    const correct = this.items.filter((i) => i.correct).length;
    const byTopic = new Map<TopicId, Tally>();
    const bySubskill = new Map<SubskillId, Tally>();

    for (const item of this.items) {
      const t = byTopic.get(item.question.topic) ?? { correct: 0, total: 0 };
      t.total++;
      if (item.correct) t.correct++;
      byTopic.set(item.question.topic, t);

      for (const sk of item.question.subskills) {
        const s = bySubskill.get(sk) ?? { correct: 0, total: 0 };
        s.total++;
        if (item.correct) s.correct++;
        bySubskill.set(sk, s);
      }
    }

    return {
      label: scopeLabel(this.config.scope),
      total,
      correct,
      percent: total > 0 ? Math.round((correct / total) * 100) : 0,
      byTopic,
      bySubskill,
      missed: this.items.filter((i) => !i.correct),
    };
  }
}
